import React, { useState, FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';

import { useConfig } from '@browsing/config';
import { transformCartForHeader } from '@browsing/utils';
import { screenSizes } from '@lux/tokens';
import { useWindowWidth } from '@lux/hooks';
import { ExpiredOverlay, CartStatusEnum } from '@pegasus/cart';
import { CommercialMessage } from '@pegasus/commercial-messages';
import { LanguageCode, TranslateScope } from '@pegasus/translation';
import { Header, NavigationItem } from '@pegasus/ui-headers';
import { useIsEligibleForVoucher } from '@pegasus/platform-journeys';
import { isSmartBannerClosed } from '@pegasus/smartbanner';
import { isClient, getSessionStorageItem } from '@pegasus/utils';

import { isMoreThanDaysAgo, isUpcomingEnabled } from '@client/helpers';
import { useZalandoPlus } from '@client/components/mylounge/plus';
import { useCart } from '@client/lib/cart/hooks';
import { getWebviewOs } from '@client/redux/modules/clientDevice/selectors';
import { getToken } from '@client/redux/modules/user/selectors';
import {
    getHeaderNotificationBanner,
    showHeaderSuccessFlashMessage,
    showHeaderErrorFlashMessage,
} from '@client/redux/modules/flashMessage';
import {
    getEmail,
    getFirstName,
    getAreCredentialsCompromised,
    getCustomerAcr,
} from '@client/redux/modules/customer';
import { UspSlider } from '@client/components/mylounge/usp/uspSlidder';
import { trackEvents, M1_REBRANDING } from '@client/components/mylounge/constants';
import useCustomCommercialMessage from '@client/components/mylounge/banner/use-commercial-message';
import { track } from '@client/helpers/tracking';
import { getCookie, setCookie, ONE_DAY } from '@client/helpers/cookie';
import SmartBanner from '@client/components/shared/SmartBanner';
import { USP_VISIBILITY_START_DATE } from '@client/components/constants';
import { SecurityBanner } from '@client/components/shared/SecurityBanner';
import { useVariant } from '@client/lib/assignments';
import { useBrazeContext } from '@pegasus/braze';

import { CategoryTabIds } from '@shared/constants';
import { useKeepArticlesInCartFeature } from '@browsing/hooks';
import { startSessionReplay, stopSessionReplay } from '@pegasus/sentry';
import { useSubscribeToNewsletter, useIsPlatformJourney } from '../mylounge/platform-journeys';
import { getCredentialsStatus, getTopPosition, isMobileOrTablet } from './helpers';
import { Timer } from './timer';
import FlashMessages from './flash-messages';
import Notifications from './notifications';
import { TabsWrapper } from '../mylounge/banner/Tabs/TabsWrapper';
import {
    HeaderWrap,
    TabsContainer,
    HeaderRowsHolder,
    HeaderBannerRow,
} from './headerWrapper.styles';
import { BrazeBanner } from '../brazeCommunication/brazeBanner/BrazeBanner';

type Props = {
    supportedLanguages?: {
        countryCode: string;
        url: string;
        active: boolean;
    }[];
    updateApplicationContext?: (isSmartbannerShown: boolean) => void;
};

type TimerProps = {
    isFocused: boolean;
    cartLength: number;
};

const LoadableDatePicker = loadable(
    () => import(/* webpackChunkName: "date-picker" */ './date-picker'),
);

const shouldEnableSearch = (isMobileView: boolean) => {
    const isSearchEnabledBasedOnCookie = !!getCookie('zl_search_in_header');

    return isMobileView && isSearchEnabledBasedOnCookie;
};

export const HeaderWrapper: FunctionComponent<Props> = ({
    updateApplicationContext,
    supportedLanguages,
}) => {
    const pathName = window.location.pathname;
    const dispatch = useDispatch();
    const config = useConfig();
    const email = useSelector(getEmail);
    const firstName = useSelector(getFirstName);
    const isApmCommunicationEnabled = config.isEnabled('isAPM');
    const hasCompromisedCredentials = useSelector(getAreCredentialsCompromised);
    const customerAcr = useSelector(getCustomerAcr);
    const isPlatformJourneyEnabled = useIsPlatformJourney();
    const isEligibleForVoucher = useIsEligibleForVoucher();
    const isWebview = !!useSelector(getWebviewOs);
    const showTabsAndCommercialMsg =
        pathName.startsWith('/event') && pathName !== `/event/${CategoryTabIds.NEW_DEALS_TODAY}`;
    const isDoi = config.isEnabled('doi');
    const [timerState, setTimerState] = useState<CartStatusEnum>(null);
    const isM1Rebranding = config.isEnabled(M1_REBRANDING);
    const commercialMessage = useCustomCommercialMessage();
    const { localeCode } = config;
    const languageCode = localeCode.replace('_', '-') as LanguageCode;

    const [isBannerShown, setIsBannerShown] = useState(true);
    const [isBannerShouldBeRemovedInDom, setIsBannerShouldBeRemovedInDom] = useState(() => {
        if (isMobileOrTablet()) {
            return isSmartBannerClosed('mylounge');
        }
        return true;
    });

    const bannerRef = useRef<HTMLDivElement>(null);
    const [bannerHeight, setBannerHeight] = useState<number>(60);

    useEffect(() => {
        const element = bannerRef.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry.contentRect.height > 0) {
                setBannerHeight(Math.round(entry.contentRect.height));
            }
        });

        resizeObserver.observe(element);
        if (element.offsetHeight > 0) {
            setBannerHeight(element.offsetHeight);
        }

        return () => resizeObserver.disconnect();
    }, [isBannerShown]);

    const screenWidth = useWindowWidth();

    const [{ cart }, cartActions] = useCart();
    const cartInfo = cart && {
        savings: cart.savings,
        total: cart.total,
        shippingCosts: cart.shippingCosts,
        discountPercent: cart.discountPercent,
        createdAt: cart.createdAt,
    };
    const cartItems = (cart && cart.items) || [];
    const csrfToken = useSelector(getToken);
    const headerNotificationBanner = useSelector(getHeaderNotificationBanner);
    const subscribeToNewsletter = useSubscribeToNewsletter();

    const { isEnabled: isPlusEnabled } = useZalandoPlus();

    const extendCart = useCallback(() => {
        cartActions.onExtendCart();
        setTimerState(null);
    }, [cartActions]);

    const onReserveCart = useCallback(async () => {
        const reservedCart = await cartActions.onReserveCart();
        return transformCartForHeader(reservedCart, config);
    }, [cartActions, config]);

    const renderTimer = useCallback(
        ({ isFocused, cartLength }: TimerProps) => (
            <Timer
                setTimerState={setTimerState}
                onExpiredCart={cartActions.onExpiredCart}
                cart={cart}
                isFocused={isFocused}
                cartLength={cartLength}
            />
        ),
        [cart, cartActions.onExpiredCart],
    );

    const handleComponentRemovalState = useCallback(() => {
        setIsBannerShown(false);
        setIsBannerShouldBeRemovedInDom(true);
    }, [setIsBannerShouldBeRemovedInDom, setIsBannerShown]);

    const handleListenToBannerPosition = useCallback((isShown: boolean) => {
        setIsBannerShown(isShown);
    }, []);

    const [showUSPVisibility] = useState(() =>
        isMoreThanDaysAgo(window.localStorage.getItem(USP_VISIBILITY_START_DATE)),
    );

    useEffect(() => {
        if (updateApplicationContext) {
            updateApplicationContext(!isBannerShouldBeRemovedInDom && isBannerShown);
        }
    }, [updateApplicationContext, isBannerShouldBeRemovedInDom, isBannerShown]);

    const securityBannerViewedCookie = getCookie('securityBannerViewed');
    const [showSecurityBanner, setShowSecurityBanner] = useState(false);

    useEffect(() => {
        if (!securityBannerViewedCookie) {
            setShowSecurityBanner(true);
        } else {
            setShowSecurityBanner(false);
        }
    }, [securityBannerViewedCookie, showSecurityBanner]);

    const handleVisibilityOnClick = () => {
        setShowSecurityBanner(!showSecurityBanner);
        setCookie('securityBannerViewed', 'true', ONE_DAY);
    };

    const isSecurityBanner =
        showSecurityBanner && isApmCommunicationEnabled && hasCompromisedCredentials;

    const { cardMethodsFromBraze } = useBrazeContext();
    const [areBrazeMethodsAvailable, setAreBrazeMethodsAvailable] = useState(false);
    useEffect(() => {
        if (cardMethodsFromBraze) {
            setAreBrazeMethodsAvailable(true);
        }
    }, [cardMethodsFromBraze]);

    const isCartItemAndBrazeVoucher =
        cartItems.length > 0 && window.location.search.includes('&brazeVoucher=true');
    const isBrazeVoucherExperience = isCartItemAndBrazeVoucher ? NavigationItem.cart : null;
    const isBrazeSnackbar = isCartItemAndBrazeVoucher || false;

    const variantSearchEnabled = !!useVariant('lounge_enable_search_feature', 'variant_search_on');
    const isSearchExperimentEnabled = useMemo(() => {
        const isMobileView = screenWidth < screenSizes.sm;
        if (variantSearchEnabled && isMobileView) {
            return true;
        }
        return shouldEnableSearch(isMobileView);
    }, [variantSearchEnabled, screenWidth]);

    const isKeepArticlesInCartEnabled = useKeepArticlesInCartFeature();

    if (isWebview) {
        return null;
    }

    const showBannerAfterDismissal =
        isClient() && getSessionStorageItem('show_braze_voucher_dismissed');
    const cardFromSessionStorageString = isClient() && getSessionStorageItem('brazeCardToRender');
    const cardFromSessionStorage =
        cardFromSessionStorageString && JSON.parse(cardFromSessionStorageString);

    const bannerAllowedByCookie = getCookie('show_braze_banner');
    const canBrazeBannerBeDisplayed =
        showBannerAfterDismissal &&
        bannerAllowedByCookie &&
        cardFromSessionStorage &&
        areBrazeMethodsAvailable;

    const VoucherBanner = canBrazeBannerBeDisplayed && (
        <BrazeBanner
            cardToRender={cardFromSessionStorage}
            cardMethodsFromBraze={cardMethodsFromBraze}
        />
    );

    return (
        <HeaderWrap
            data-testid="header"
            movePositionTopBy={getTopPosition(
                isM1Rebranding,
                showUSPVisibility,
                isBannerShouldBeRemovedInDom,
                isBannerShown,
                bannerHeight,
            )}
        >
            <HeaderRowsHolder>
                {!isBannerShouldBeRemovedInDom && (
                    <HeaderBannerRow height={bannerHeight}>
                        <SmartBanner
                            ref={bannerRef}
                            listenToBannerPosition={handleListenToBannerPosition}
                            updateComponentRemovalState={handleComponentRemovalState}
                        />
                    </HeaderBannerRow>
                )}
                <UspSlider isM1Rebranding={isM1Rebranding} showUSPVisibility={showUSPVisibility} />
                <Header
                    appDomain={config.appdomain}
                    languageCode={languageCode}
                    onNavigationChange={navigationItem => {
                        if (navigationItem === NavigationItem.cart && isKeepArticlesInCartEnabled)
                            cartActions.onCartIconClick();
                    }}
                    csrfToken={csrfToken}
                    timer={renderTimer}
                    cart={{
                        items: cartItems,
                        info: cartInfo,

                        appendComponent: VoucherBanner,
                        ...(cart?.unavailableItems && {
                            unavailableItems: cart.unavailableItems,
                        }),
                        ...(cart?.cartType && { cartType: cart.cartType }),
                        ...(cart?.isCartFromUnreservedCart && {
                            isCartFromUnreservedCart: cart.isCartFromUnreservedCart,
                        }),
                        ...(cart?.unavailableItemsCount && {
                            unavailableItemsCount: cart.unavailableItemsCount,
                        }),
                    }}
                    onOpen={(navigationItem: NavigationItem) => {
                        if (navigationItem === NavigationItem.cart) {
                            // startSessionReplay();
                        }
                    }}
                    onClose={(navigationItem: NavigationItem) => {
                        if (navigationItem === NavigationItem.cart) {
                            // stopSessionReplay();
                        }
                    }}
                    cartEvents={{
                        onItemRemove: cartActions.onItemRemove,
                        onReserveCart,
                    }}
                    datePicker={isUpcomingEnabled(config) ? <LoadableDatePicker /> : null}
                    supportedLanguages={supportedLanguages}
                    isPlusEnabled={isPlusEnabled}
                    isPrive={config.isPriveDomain}
                    isPlatformJourneyEnabled={isPlatformJourneyEnabled}
                    showSuccessMessage={(message: string) =>
                        dispatch(showHeaderSuccessFlashMessage(message))
                    }
                    showErrorMessage={(message: string) =>
                        dispatch(showHeaderErrorFlashMessage(message))
                    }
                    isDOIUser={isDoi}
                    isSubscribedToNewsletter={!isEligibleForVoucher}
                    isNwlDialogEnabled
                    subscribeToNewsletter={subscribeToNewsletter}
                    email={email}
                    notification={headerNotificationBanner}
                    firstName={firstName}
                    openOnMount={isBrazeVoucherExperience}
                    isSearchEnabled={isSearchExperimentEnabled}
                    mode="static"
                    maximumFractionDigits={config.maximumFractionDigits}
                    isKeepArticleInCartEnabled={isKeepArticlesInCartEnabled}
                />
                {!isM1Rebranding && (
                    <>
                        {isBrazeSnackbar && <FlashMessages hasSmartBanner={isBrazeSnackbar} />}
                        <HeaderBannerRow height="auto">
                            {!isBrazeSnackbar && <FlashMessages />}
                            <Notifications />
                        </HeaderBannerRow>
                    </>
                )}
            </HeaderRowsHolder>

            {isSecurityBanner && (
                <SecurityBanner
                    handleVisibilityOnClick={handleVisibilityOnClick}
                    userEmail={email}
                />
            )}

            {!isSecurityBanner && isM1Rebranding && showTabsAndCommercialMsg && (
                <TranslateScope path="myLounge">
                    {isBrazeSnackbar && <FlashMessages hasSmartBanner={isBrazeSnackbar} />}
                    <TabsContainer isBrazeSnackbar={isBrazeSnackbar}>
                        {!isBrazeSnackbar && <FlashMessages />}
                        <Notifications />
                        <TabsWrapper />
                        {!isPlatformJourneyEnabled &&
                            commercialMessage?.isEnabled &&
                            !commercialMessage?.isModal && (
                                <TransitionGroup className="transition__group">
                                    {commercialMessage.isShown && (
                                        <CSSTransition
                                            classNames="commercial-transition"
                                            timeout={300}
                                        >
                                            <CommercialMessage
                                                event={commercialMessage.event}
                                                onRedeem={commercialMessage.onRedeem}
                                                onClose={commercialMessage.onClose}
                                                track={track}
                                                trackEvents={trackEvents}
                                            />
                                        </CSSTransition>
                                    )}
                                </TransitionGroup>
                            )}
                    </TabsContainer>
                </TranslateScope>
            )}

            <ExpiredOverlay
                isShown={!!timerState}
                status={timerState}
                closeDialog={() => {
                    setTimerState(null);
                }}
                extendCart={extendCart}
            />
        </HeaderWrap>
    );
};

export default HeaderWrapper;
